import React, { useState, useEffect } from 'react';
import { Calendar, Store } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import API_BASE_URL from '../services/apiconfig';

const TodaysPrice = () => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/todays_price/get_todays_price`);
      const result = await res.json();
      if (result.status === 'success' && result.data) {
        setMeta(result.data.meta);
        setItems(result.data.items || []);
      }
    } catch (e) {
      console.error('Error fetching price list:', e);
    } finally {
      setLoading(false);
    }
  };

  const t = {
    en: {
      title: "Today's Price",
      subtitle: 'Daily vegetable price list from Cooperative Store',
      sno: '#',
      product: 'Vegetables',
      unit: 'Qty',
      price: '₹',
      date: 'Date',
      noData: 'Price list not available at the moment.',
      disclaimer: 'Prices are updated daily and may vary.',
    },
    ta: {
      title: 'இன்றைய விலை',
      subtitle: 'கூட்டுறவு கடை தினசரி காய்கறி விலைப் பட்டியல்',
      sno: '#',
      product: 'காய்கறிகள்',
      unit: 'அளவு',
      price: '₹',
      date: 'தேதி',
      noData: 'விலைப் பட்டியல் தற்போது கிடைக்கவில்லை.',
      disclaimer: 'விலைகள் தினமும் புதுப்பிக்கப்படும்; மாறலாம்.',
    },
  }[language];

  const pickLang = (en, ta) => (language === 'en' ? en || '' : ta || en || '');

  if (loading) {
    return (
      <section className="py-8 bg-white">
        <div className="container mx-auto px-4 text-center text-gray-500">Loading price list…</div>
      </section>
    );
  }

  // Hide section if no data
  if (!items || items.length === 0) return null;

  // Split items into two halves for 2-column layout
  const half = Math.ceil(items.length / 2);
  const col1 = items.slice(0, half);
  const col2 = items.slice(half);

  const renderRow = (item, globalIdx) => (
    <tr
      key={item.id}
      className={`${globalIdx % 2 === 0 ? 'bg-white/80' : 'bg-green-50/60'} hover:bg-green-100/70 transition-colors border-t border-green-100`}
    >
      <td className="px-2 py-1.5 text-xs text-gray-500 text-center font-medium w-8">{globalIdx + 1}</td>
      <td className="px-2 py-1.5 text-xs sm:text-sm text-gray-800 font-medium">
        {pickLang(item.name_eng, item.name_tamil)}
      </td>
      <td className="px-2 py-1.5 text-xs text-gray-600 whitespace-nowrap">
        {pickLang(item.unit_eng, item.unit_tamil)}
      </td>
      <td className="px-2 py-1.5 text-sm text-green-800 font-bold text-right whitespace-nowrap">
        ₹{item.price}
      </td>
    </tr>
  );

  return (
    <section
      className="relative py-10 overflow-hidden"
      style={{
        backgroundColor: '#f7fdf9',
        backgroundImage: `
          radial-gradient(circle at 10% 10%, rgba(34, 197, 94, 0.05) 0%, transparent 30%),
          radial-gradient(circle at 90% 90%, rgba(249, 115, 22, 0.04) 0%, transparent 30%)
        `,
      }}
    >
      {/* Faint corner emoji accents (not tiled) */}
      <div className="pointer-events-none select-none absolute top-6 right-6 text-5xl opacity-[0.05]">🥕</div>
      <div className="pointer-events-none select-none absolute bottom-6 left-8 text-5xl opacity-[0.05]">🍅</div>
      <div className="pointer-events-none select-none absolute top-1/2 left-4 text-4xl opacity-[0.04]">🥬</div>
      <div className="pointer-events-none select-none absolute top-1/2 right-6 text-4xl opacity-[0.04]">🍋</div>
      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-5">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800">{t.title}</h2>
          <div className="w-16 h-1 bg-green-600 mx-auto mt-2 mb-2"></div>
          <p className="text-sm text-gray-600">{t.subtitle}</p>
        </div>

        {/* Store + Date */}
        {meta && (
          <div className="max-w-5xl mx-auto bg-white/90 backdrop-blur-sm rounded-lg shadow-sm border border-green-200 px-4 py-3 mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
            <div className="flex items-start gap-2 text-sm">
              <Store className="w-4 h-4 text-green-700 flex-shrink-0 mt-0.5" />
              <span className="font-semibold text-gray-800">
                {pickLang(meta.store_name_eng, meta.store_name_tamil)}
              </span>
            </div>
            {meta.price_date && (
              <div className="flex items-center gap-1.5 text-xs bg-green-600 text-white px-3 py-1 rounded font-semibold whitespace-nowrap">
                <Calendar className="w-3.5 h-3.5" />
                {t.date}: {meta.price_date}
              </div>
            )}
          </div>
        )}

        {/* Two-column price table */}
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-3">
          {[col1, col2].map((col, colIdx) => (
            <div
              key={colIdx}
              className="bg-white/90 backdrop-blur-sm shadow-sm rounded-lg border border-green-200 overflow-hidden"
            >
              <table className="w-full">
                <thead className="bg-green-700 text-white">
                  <tr>
                    <th className="px-2 py-2 text-center font-semibold text-xs w-8">{t.sno}</th>
                    <th className="px-2 py-2 text-left font-semibold text-xs">{t.product}</th>
                    <th className="px-2 py-2 text-left font-semibold text-xs w-20">{t.unit}</th>
                    <th className="px-2 py-2 text-right font-semibold text-xs w-16">{t.price}</th>
                  </tr>
                </thead>
                <tbody>{col.map((it, i) => renderRow(it, colIdx === 0 ? i : half + i))}</tbody>
              </table>
            </div>
          ))}
        </div>

        <p className="text-center text-xs text-gray-500 italic mt-3">* {t.disclaimer}</p>
      </div>
    </section>
  );
};

export default TodaysPrice;
